import { mapActions } from 'vuex';

import { api } from '_/services/api';

/*
 * Prediction management dashboard ([3.20]).
 *
 * Read-only summary of recently-finished + upcoming matches with the
 * pick distribution and resolution state. The "Resolve" button calls
 * the PredictionResolver service (idempotent: re-running on an
 * already-resolved match is a no-op). Drill-down panel shows the
 * per-user prediction list so editors can sanity-check the resolver
 * against the inferred outcome.
 *
 * Polling is intentionally absent — this dashboard is operated on
 * demand when something needs attention, not as a live ticker. The
 * Refresh button is explicit.
 */

function formatRelative (iso) {
    if (!iso) return '—';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const dt = Date.now() - t;
    if (dt < 0) {
        // Future timestamp — kickoffs etc.
        const future = -dt;
        if (future < 3_600_000)  return 'in ' + Math.floor(future / 60_000) + 'm';
        if (future < 86_400_000) return 'in ' + Math.floor(future / 3_600_000) + 'h';
        return 'in ' + Math.floor(future / 86_400_000) + 'd';
    }
    if (dt < 60_000)     return Math.floor(dt / 1000) + 's ago';
    if (dt < 3_600_000)  return Math.floor(dt / 60_000) + 'm ago';
    if (dt < 86_400_000) return Math.floor(dt / 3_600_000) + 'h ago';
    return Math.floor(dt / 86_400_000) + 'd ago';
}

function statusBadge (status) {
    switch (status) {
        case 'full_time': return 'label-success';
        case 'live':
        case 'half_time': return 'label-danger';
        case 'scheduled': return 'label-info';
        default:          return 'label-default';
    }
}

function predictionLabel (p) {
    switch (p) {
        case 'home_win': return '1';
        case 'draw':     return 'X';
        case 'away_win': return '2';
        default:         return p || '—';
    }
}

function stateBadge (state) {
    switch (state) {
        case 'resolved_correct': return 'label-success';
        case 'resolved_wrong':   return 'label-default';
        case 'unresolved':       return 'label-warning';
        default:                 return 'label-default';
    }
}

function stateLabel (state) {
    switch (state) {
        case 'resolved_correct': return 'correct';
        case 'resolved_wrong':   return 'wrong';
        case 'unresolved':       return 'unresolved';
        default:                 return state;
    }
}

export default {
    name: 'PredictionManagement',

    data () {
        return {
            asOf: null,
            matches: [],
            loading: false,
            error: null,
            // Drill-down state
            selected: null,            // sportradar_id
            selectedMatch: null,       // match envelope from /match endpoint
            selectedPredictions: [],
            detailLoading: false,
            detailError: null,
            // Per-row action state
            resolving: {},             // { [sportradar_id]: true }
            resolveResult: null,       // { sportradarId, body } banner
            resolveError: null,
        };
    },

    computed: {
        breadcrumbItems () {
            return [{
                text: 'Prediction Management',
                link: 'prediction-management',
            }];
        },

        attentionCount () {
            return this.matches.filter(m => m.needs_attention).length;
        },
        totalPredictions () {
            return this.matches.reduce((acc, m) => acc + (m.predictions.total || 0), 0);
        },
    },

    methods: {
        ...mapActions('ui', [
            'animateWrapper',
            'setBodyClass',
            'setBreadcrumbItems',
        ]),

        formatRelative,
        statusBadge,
        predictionLabel,
        stateBadge,
        stateLabel,

        async refresh () {
            this.loading = true;
            this.error = null;
            try {
                const response = await api.get('admin/predictions/summary');
                // PCMS double-wraps standard controller responses
                // (outer {message, body} envelope, then an inner
                // {message, body: …} from the api client). Unwrap both
                // layers; fall back to a single unwrap or the raw
                // response for any other shape.
                const outer = response && response.body ? response.body : response;
                const payload = outer && outer.body ? outer.body : outer;
                this.matches = (payload && payload.matches) || [];
                this.asOf = (payload && payload.as_of) || null;
            } catch (exc) {
                this.error = (exc && exc.message)
                    || (exc && exc.response && exc.response.data && exc.response.data.message)
                    || 'Could not load prediction summary.';
            } finally {
                this.loading = false;
            }
        },

        async openDetails (sportradarId) {
            if (this.selected === sportradarId) {
                // Toggle off
                this.selected = null;
                this.selectedMatch = null;
                this.selectedPredictions = [];
                return;
            }
            this.selected = sportradarId;
            this.selectedMatch = null;
            this.selectedPredictions = [];
            this.detailError = null;
            this.detailLoading = true;
            try {
                const response = await api.get(
                    'admin/predictions/match/' + encodeURIComponent(sportradarId)
                );
                // PCMS double-wraps standard controller responses
                // (outer {message, body} envelope, then an inner
                // {message, body: …} from the api client). Unwrap both
                // layers; fall back to a single unwrap or the raw
                // response for any other shape.
                const outer = response && response.body ? response.body : response;
                const payload = outer && outer.body ? outer.body : outer;
                this.selectedMatch = payload && payload.match;
                this.selectedPredictions = (payload && payload.predictions) || [];
            } catch (exc) {
                this.detailError = (exc && exc.message)
                    || 'Could not load match details.';
            } finally {
                this.detailLoading = false;
            }
        },

        async resolve (sportradarId) {
            if (this.resolving[sportradarId]) return;
            // eslint-disable-next-line no-alert
            if (!window.confirm(
                'Re-run resolution for this match? Idempotent — safe to run even if predictions are already resolved.'
            )) return;

            this.$set(this.resolving, sportradarId, true);
            this.resolveResult = null;
            this.resolveError = null;
            try {
                const response = await api.post(
                    'admin/predictions/resolve/' + encodeURIComponent(sportradarId),
                    {}
                );
                // PCMS double-wraps standard controller responses
                // (outer {message, body} envelope, then an inner
                // {message, body: …} from the api client). Unwrap both
                // layers; fall back to a single unwrap or the raw
                // response for any other shape.
                const outer = response && response.body ? response.body : response;
                const payload = outer && outer.body ? outer.body : outer;
                this.resolveResult = { sportradarId, body: payload };
                // Refresh summary so counters reflect the new state.
                await this.refresh();
                // If the drill-down is open for this match, reload it too.
                if (this.selected === sportradarId) {
                    this.selected = null; // force re-open
                    await this.openDetails(sportradarId);
                }
            } catch (exc) {
                this.resolveError = (exc && exc.message)
                    || (exc && exc.response && exc.response.data && exc.response.data.error)
                    || 'Resolve failed.';
            } finally {
                this.$set(this.resolving, sportradarId, false);
            }
        },
    },

    mounted () {
        this.$nextTick(function () {
            this.setBodyClass('admin');
            this.setBreadcrumbItems(this.breadcrumbItems);
            this.animateWrapper();
            this.refresh();
        });
    },
};
